import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CommunityService } from '@common/services/community.service';
import { API_URL, BUSINESS } from '@common/app/app.config';

import { ADDRESS_SERVICE_URL } from '../app.config';
import { City, JapanAddress, Prefecture } from '../model/japan-address';
import { excludedHokkaidoZips } from './excluded.hokkaido.zips';
import { joinRanges } from '../helpers';

export enum BusinessCode {
    ALL,
    ENJP,
    SPJP,
}

@Injectable()
export class JapanAddressService {
    // Format:
    // <FROM> businessCode-communityCode : <TO> communityCode
    public static readonly CommunityMapping = {
        'enjp-tokyo': 'shutoken',
    };

    // List of TMG communities used across whole app
    public static readonly TmgCommunities = [
        'shutoken',
        'saitama-prefecture',
        'chiba-prefecture',
        'kanagawa-prefecture',
        'chiba-city',
        'kawasaki-city',
        'sagamihara-city',
        'saitama-city',
        'yokohama-city',
        'tochigi-prefecture',
        'others',
    ];

    // For tests
    public static readonly QACommunities = [
        'qa-test-r',
        'qa-test-rap',
        'qa-test-business',
    ];

    sublocalities_BLACKLIST = [
        '以下に掲載がない場合', // not real locality name, means 'If not listed below' and is a placeholder in data source
    ];

    private zipMatrix: ZipcodeEntry[] = [
        // *** iChoosr ***
        new ZipcodeEntry(
            BusinessCode.ALL,
            'ichoosr',
            '100-0000',
            '399-8000',
            ['222-0001'],
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ALL,
            'qa-test',
            '100-0000',
            '399-8000',
            ['222-0001'],
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ALL,
            'qa-test-a',
            '100-0000',
            '399-8000',
            ['222-0001'],
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ALL,
            'qa-test-business',
            '100-0000',
            '399-8000',
            ['222-0001'],
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ALL,
            'qa-test-ap',
            '100-0000',
            '399-8000',
            ['222-0001'],
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ALL,
            'qa-test-apf',
            '100-0000',
            '399-8000',
            ['222-0001'],
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ALL,
            'qa-test-c',
            '100-0000',
            '399-8000',
            ['222-0001'],
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ALL,
            'qa-test-f',
            '100-0000',
            '399-8000',
            ['222-0001'],
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ALL,
            'qa-test-null',
            '100-0000',
            '399-8000',
            ['222-0001'],
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ALL,
            'qa-test-r',
            '100-0000',
            '399-8000',
            ['222-0001'],
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ALL,
            'qa-test-ra',
            '100-0000',
            '399-8000',
            ['222-0001'],
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ALL,
            'qa-test-ran',
            '100-0000',
            '399-8000',
            ['222-0001'],
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ALL,
            'qa-test-rap',
            '100-0000',
            '399-8000',
            ['222-0001'],
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ALL,
            'qa-test-rapn',
            '100-0000',
            '399-8000',
            ['222-0001'],
            false,
        ),

        // ***** BusinessCode.ENJP *****

        // *** Shutoken ***
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'shutoken',
            '100-0000',
            '389-0121',
            null,
            true,
        ),

        // *** Suita ***
        // Mie prefecture
        // -- Kumano city
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '519-4201',
            '519-5417',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '647-1321',
            '647-1325',
            null,
            false,
        ),
        // -- Kicho cho
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '519-5700',
            '519-5835',
            null,
            false,
        ),
        // -- Mihama machi
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '519-5200',
            '519-5327',
            null,
            false,
        ),

        // Shiga pefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '520-0000',
            '529-1851',
            null,
            false,
        ),

        // Osaka prefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '530-0000',
            '599-8280',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '618-0000',
            '618-0024',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '630-0271',
            '630-0272',
            null,
            false,
        ),

        // Kyoto prefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '600-0000',
            '629-3579',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '520-0461',
            '520-0465',
            null,
            false,
        ),

        // Nara prefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '630-0000',
            '648-0309',
            null,
            false,
        ),

        // Wakayama prefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '640-0000',
            '649-7216',
            null,
            false,
        ),

        // Hyogo prefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '650-0000',
            '679-5654',
            ['678-0257'],
            false,
        ),

        // Fukui prefecture
        // -- Mihama cho
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '919-1100',
            '919-1208',
            null,
            false,
        ),
        // -- Wakasa cho
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '919-1300',
            '919-1559',
            null,
            false,
        ),
        // -- Kohama city
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '917-0000',
            '917-0357',
            null,
            false,
        ),
        // -- Ooi cho
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '917-0361',
            '917-0387',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '919-2100',
            '919-2135',
            null,
            false,
        ),
        // -- Takahama cho
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '919-2200',
            '919-2387',
            null,
            false,
        ),

        // Gifu prefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'suita',
            '503-1500',
            '503-1545',
            null,
            false,
        ),

        // *** Kyoto (same as Suita) ***
        // Mie prefecture
        // -- Kumano city
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '519-4201',
            '519-5417',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '647-1321',
            '647-1325',
            null,
            true,
        ),
        // -- Kicho cho
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '519-5700',
            '519-5835',
            null,
            true,
        ),
        // -- Mihama machi
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '519-5200',
            '519-5327',
            null,
            true,
        ),

        // Shiga pefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '520-0000',
            '529-1851',
            null,
            true,
        ),

        // Osaka prefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '530-0000',
            '599-8280',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '618-0000',
            '618-0024',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '630-0271',
            '630-0272',
            null,
            true,
        ),

        // Kyoto prefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '600-0000',
            '629-3579',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '520-0461',
            '520-0465',
            null,
            true,
        ),

        // Nara prefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '630-0000',
            '648-0309',
            null,
            true,
        ),

        // Wakayama prefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '640-0000',
            '649-7216',
            null,
            true,
        ),

        // Hyogo prefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '650-0000',
            '679-5654',
            ['678-0257'],
            true,
        ),

        // Fukui prefecture
        // -- Mihama cho
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '919-1100',
            '919-1208',
            null,
            true,
        ),
        // -- Wakasa cho
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '919-1300',
            '919-1559',
            null,
            true,
        ),
        // -- Kohama city
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '917-0000',
            '917-0357',
            null,
            true,
        ),
        // -- Ooi cho
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '917-0361',
            '917-0387',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '919-2100',
            '919-2135',
            null,
            true,
        ),
        // -- Takahama cho
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '919-2200',
            '919-2387',
            null,
            true,
        ),

        // Gifu prefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kyoto',
            '503-1500',
            '503-1545',
            null,
            true,
        ),

        // TMG3 Prefectures and Cities (ignored as alternates)
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'others',
            '100-0000',
            '399-9999',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'tokyo',
            '100-0000',
            '399-9999',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'saitama-prefecture',
            '100-0000',
            '399-9999',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'chiba-prefecture',
            '100-0000',
            '399-9999',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kanagawa-prefecture',
            '100-0000',
            '399-9999',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'yokohama-city',
            '100-0000',
            '399-9999',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'kawasaki-city',
            '100-0000',
            '399-9999',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'sagamihara-city',
            '100-0000',
            '399-9999',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'saitama-city',
            '100-0000',
            '399-9999',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'chiba-city',
            '100-0000',
            '399-9999',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'tochigi-prefecture',
            '100-0000',
            '399-9999',
            null,
            false,
        ),

        // *** Fukuoka City  see: long story here: https://ichoosr.atlassian.net/browse/ENJP-853 ***
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'fukuoka-city',
            '800-0000',
            '811-4343',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'fukuoka-city',
            '812-0000',
            '816-0983',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'fukuoka-city',
            '818-0000',
            '819-0007',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'fukuoka-city',
            '819-0012',
            '889-4602',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'fukuoka-city',
            '890-0001',
            '890-0086',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'fukuoka-city',
            '891-0101',
            '891-2322',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'fukuoka-city',
            '892-0801',
            '893-2604',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'fukuoka-city',
            '895-0000',
            '896-0079',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'fukuoka-city',
            '897-0000',
            '899-8609',
            null,
            true,
        ),

        // *** Both Nagano and Gifu prefectures are in Chubu Region, so they use same zipcode ranges See more info : https://ichoosr.atlassian.net/browse/ENJP-936 ***
        // *** Nagano City ***
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '380-0800',
            '399-9601',
            ['384-0097', '389-2261', '389-0121'],
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '949-8321',
            '949-8321',
            null,
            true,
        ),

        // *** Gifu 4 nagano-city community ***
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '500-0000',
            '500-0000',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '500-8001',
            '500-8089',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '500-8101',
            '500-8189',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '500-8207',
            '500-8289',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '500-8301',
            '500-8389',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '500-8401',
            '500-8489',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '500-8801',
            '500-8891',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '501-0101',
            '501-6338',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '502-0001',
            '502-0939',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '503-0000',
            '503-2506',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '504-0000',
            '504-0971',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '505-0000',
            '505-0532',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '506-0000',
            '506-2258',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '507-0000',
            '507-0901',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '508-0000',
            '508-0502',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '509-0101',
            '509-9232',
            null,
            true,
        ),

        // *** Aichi 4 nagano-city community ***
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '431-4121',
            '431-4121',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '440-0001',
            '440-0897',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '441-0001',
            '441-8157',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '442-0000',
            '442-0889',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '443-0000',
            '443-0106',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '444-0000',
            '444-3626',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '445-0000',
            '445-0894',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '446-0000',
            '446-0076',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '447-0000',
            '447-0889',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '448-0000',
            '448-0862',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '449-0000',
            '449-0405',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '450-0000',
            '450-6690',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '451-0000',
            '451-6090',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '452-0000',
            '452-0963',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '453-0000',
            '453-6190',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '454-0000',
            '454-0997',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '455-0000',
            '455-0887',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '456-0000',
            '456-0078',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '457-0000',
            '457-0866',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '458-0000',
            '458-0929',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '459-8001',
            '459-8017',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '460-0000',
            '460-0032',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '461-0000',
            '461-0049',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '462-0000',
            '462-0869',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '463-0000',
            '463-0815',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '464-0000',
            '464-0871',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '465-0000',
            '465-0097',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '466-0000',
            '466-0858',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '467-0000',
            '467-0879',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '468-0000',
            '468-0836',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '470-0000',
            '470-3505',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '471-0000',
            '497-0058',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '498-0001',
            '498-0069',
            null,
            true,
        ),

        // *** Aichi & Mie 4 nagano-city community ***
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '498-0000',
            '498-0000',
            null,
            true,
        ),

        // *** Mie 4 nagano-city community ***
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '514-0000',
            '514-0000',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '498-0801',
            '498-0824',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '510-0000',
            '519-5835',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '647-1321',
            '647-1325',
            null,
            true,
        ),

        // *** Shizuoka 4 nagano-city community ***
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '422-0000',
            '422-0000',
            null,
            true,
        ),
        // 4160901-4228078 - exclude: 4190100-4190125
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '416-0901',
            '419-0099',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'nagano-city',
            '419-0126',
            '422-8078',
            null,
            true,
        ),

        // *** Nagano City 4 Gifu community***
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '380-0800',
            '399-9601',
            ['384-0097', '389-2261', '389-0121'],
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '949-8321',
            '949-8321',
            null,
            true,
        ),

        // *** Gifu ***
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '500-0000',
            '500-0000',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '500-8001',
            '500-8089',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '500-8101',
            '500-8189',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '500-8207',
            '500-8289',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '500-8301',
            '500-8389',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '500-8401',
            '500-8489',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '500-8801',
            '500-8891',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '501-0101',
            '501-6338',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '502-0001',
            '502-0939',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '503-0000',
            '503-2506',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '504-0000',
            '504-0971',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '505-0000',
            '505-0532',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '506-0000',
            '506-2258',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '507-0000',
            '507-0901',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '508-0000',
            '508-0502',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '509-0101',
            '509-9232',
            null,
            true,
        ),

        // *** Aichi 4 gifu community ***
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '431-4121',
            '431-4121',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '440-0001',
            '440-0897',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '441-0001',
            '441-8157',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '442-0000',
            '442-0889',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '443-0000',
            '443-0106',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '444-0000',
            '444-3626',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '445-0000',
            '445-0894',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '446-0000',
            '446-0076',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '447-0000',
            '447-0889',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '448-0000',
            '448-0862',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '449-0000',
            '449-0405',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '450-0000',
            '450-6690',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '451-0000',
            '451-6090',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '452-0000',
            '452-0963',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '453-0000',
            '453-6190',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '454-0000',
            '454-0997',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '455-0000',
            '455-0887',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '456-0000',
            '456-0078',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '457-0000',
            '457-0866',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '458-0000',
            '458-0929',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '459-8001',
            '459-8017',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '460-0000',
            '460-0032',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '461-0000',
            '461-0049',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '462-0000',
            '462-0869',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '463-0000',
            '463-0815',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '464-0000',
            '464-0871',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '465-0000',
            '465-0097',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '466-0000',
            '466-0858',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '467-0000',
            '467-0879',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '468-0000',
            '468-0836',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '470-0000',
            '470-3505',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '471-0000',
            '497-0058',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '498-0001',
            '498-0069',
            null,
            true,
        ),

        // *** Aichi & Mie 4 gifu community ***
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '498-0000',
            '498-0000',
            null,
            true,
        ),

        // *** Mie 4 gifu community ***
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '514-0000',
            '514-0000',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '498-0801',
            '498-0824',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '510-0000',
            '519-5835',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '647-1321',
            '647-1325',
            null,
            true,
        ),

        // *** Shizuoka 4 gifu community ***
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '422-0000',
            '422-0000',
            null,
            true,
        ),
        // 4160901-4228078 - exclude: 4190100-4190125
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '416-0901',
            '419-0099',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'gifu',
            '419-0126',
            '422-8078',
            null,
            true,
        ),

        // *** Osaka, see: https://ichoosr.atlassian.net/browse/ENJP-940 ***
        // Mie prefecture
        // -- Kumano city
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '519-4201',
            '519-5417',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '647-1321',
            '647-1325',
            null,
            false,
        ),
        // -- Kicho cho
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '519-5700',
            '519-5835',
            null,
            false,
        ),
        // -- Mihama machi
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '519-5200',
            '519-5327',
            null,
            false,
        ),

        // Shiga pefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '520-0000',
            '529-1851',
            null,
            false,
        ),

        // Osaka prefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '530-0000',
            '599-8280',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '618-0000',
            '618-0024',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '630-0271',
            '630-0272',
            null,
            false,
        ),

        // Kyoto prefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '600-0000',
            '629-3579',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '520-0461',
            '520-0465',
            null,
            false,
        ),

        // Nara prefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '630-0000',
            '648-0309',
            null,
            false,
        ),

        // Wakayama prefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '640-0000',
            '649-7216',
            null,
            false,
        ),

        // Hyogo prefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '650-0000',
            '679-5654',
            ['678-0257'],
            false,
        ),

        // Fukui prefecture
        // -- Mihama cho
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '919-1100',
            '919-1208',
            null,
            false,
        ),
        // -- Wakasa cho
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '919-1300',
            '919-1559',
            null,
            false,
        ),
        // -- Kohama city
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '917-0000',
            '917-0357',
            null,
            false,
        ),
        // -- Ooi cho
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '917-0361',
            '917-0387',
            null,
            false,
        ),
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '919-2100',
            '919-2135',
            null,
            false,
        ),
        // -- Takahama cho
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '919-2200',
            '919-2387',
            null,
            false,
        ),

        // Gifu prefecture
        new ZipcodeEntry(
            BusinessCode.ENJP,
            'osaka',
            '503-1500',
            '503-1545',
            null,
            false,
        ),

        // -----------------------------------------------------
        // ----------------- BusinessCode.SPJP -----------------

        // *** Aichi ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'aichi',
            '470-0100',
            '470-0136',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'aichi',
            '470-0200',
            '470-0233',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'aichi',
            '491-0000',
            '491-0938',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'aichi',
            '493-0001',
            '494-0019',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'aichi',
            '470-0192',
            '470-0192',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'aichi',
            '470-0295',
            '470-0295',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'aichi',
            '491-8501',
            '491-8501',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'aichi',
            '450-0001',
            '451-6090',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'aichi',
            '452-0801',
            '452-0848',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'aichi',
            '453-0000',
            '468-0836',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'aichi',
            '488-0000',
            '488-0884',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'aichi',
            '445-0000',
            '445-0894',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'aichi',
            '488-8666',
            '488-8666',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'aichi',
            '445-8501',
            '445-8501',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'aichi',
            '444-0301',
            '444-0704',
            null,
            true,
        ),

        // *** Aichi (Business) ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-aichi',
            '450-0001',
            '451-6090',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-aichi',
            '452-0801',
            '452-0848',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-aichi',
            '453-0000',
            '468-0836',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-aichi',
            '470-0100',
            '470-0136',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-aichi',
            '470-0192',
            '470-0192',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-aichi',
            '470-0200',
            '470-0233',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-aichi',
            '470-0295',
            '470-0295',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-aichi',
            '480-1100',
            '480-1184',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-aichi',
            '480-1196',
            '480-1196',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-aichi',
            '480-1301',
            '480-1347',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-aichi',
            '491-0000',
            '491-0938',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-aichi',
            '491-8501',
            '491-8501',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-aichi',
            '493-0001',
            '494-0019',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-aichi',
            '431-4121',
            '431-4121',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-aichi',
            '440-0001',
            '498-0069',
            null,
            true,
        ),

        // *** Chiba ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'chiba',
            '260-0000',
            '299-5506',
            null,
            true,
        ),

        // *** Chiba (Business) ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-chiba',
            '260-0000',
            '299-5506',
            null,
            true,
        ),
        // *** Dummy ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'dummy-community',
            '230-0001',
            '230-0001',
            null,
            true,
        ),
        /// *** Ehime ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'ehime',
            '790-0001',
            '799-3772',
            null,
            true,
        ),

        /// *** Ehime (Business)***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-ehime',
            '790-0001',
            '799-3772',
            null,
            true,
        ),
        // *** Fukuoka ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'fukuoka',
            '800-0000',
            '811-4343',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'fukuoka',
            '812-0000',
            '816-0983',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'fukuoka',
            '818-0000',
            '839-1415',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'fukuoka',
            '871-0000',
            '871-0000',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'fukuoka',
            '871-0226',
            '871-0226',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'fukuoka',
            '871-0801',
            '871-0928',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'fukuoka',
            '812-8577',
            '812-8577',
            null,
            true,
        ),

        // *** Fukuoka (Business) ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-fukuoka',
            '800-0000',
            '811-4343',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-fukuoka',
            '812-0000',
            '816-0983',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-fukuoka',
            '818-0000',
            '839-1415',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-fukuoka',
            '871-0000',
            '871-0000',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-fukuoka',
            '871-0226',
            '871-0226',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-fukuoka',
            '871-0801',
            '871-0928',
            null,
            true,
        ),

        // *** Gifu ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'gifu',
            '500-0000',
            '509-9232',
            null,
            true,
        ),

        // *** Gifu (Business) ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-gifu',
            '500-0000',
            '509-9232',
            null,
            true,
        ),

        // *** Gunma ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'gunma',
            '370-0000',
            '379-2400',
            ['370-1507'],
            true,
        ),

        // *** Gunma (Business) ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-gunma',
            '370-0000',
            '379-2400',
            ['370-1507'],
            true,
        ),

        // *** Hiroshima ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hiroshima',
            '720-0001',
            '739-2732',
            null,
            true,
        ),

        // *** Hokkaido *** 001-0000 - 007-9999/040-0000 - 099-9999
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '001-0000',
            '007-9999',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '040-0000',
            '099-9999',
            excludedHokkaidoZips,
            true,
        ),
        // Hokkaido - Muroran-City
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '050-0000',
            '051-0036',
            null,
            true,
        ),
        // Hokkaido - Tomakomai-City
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '053-0000',
            '053-0855',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '059-1261',
            '059-1375',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '066-0271',
            '066-0271',
            null,
            true,
        ),
        // Hokkaido - Noboribetsu-City
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '059-0000',
            '059-0036',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '059-0461',
            '059-0553',
            null,
            true,
        ),
        // Hokkaido - Date-City
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '044-0441',
            '044-0444',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '052-0000',
            '052-0036',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '052-0301',
            '052-0317',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '059-0151',
            '059-0275',
            null,
            true,
        ),
        // Hokkaido - Toyouracho
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '049-5331',
            '049-5416',
            null,
            true,
        ),
        // Hokkaido - Sobetsucho
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '049-5731',
            '049-5731',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '052-0100',
            '052-0116',
            null,
            true,
        ),
        // Hokkaido - Shiraoicho
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '059-0641',
            '059-0923',
            null,
            true,
        ),
        // Hokkaido - Toyakocho
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '049-5600',
            '049-5724',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '049-5801',
            '049-5833',
            null,
            true,
        ),

        // Hokkaido - Asahikawa City
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '070-0000',
            '071-0186',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '071-1171',
            '071-1177',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '071-8101',
            '071-8171',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '074-1181',
            '074-1182',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '078-0185',
            '078-0186',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '078-1271',
            '078-1274',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '078-8201',
            '078-8825',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hokkaido',
            '079-8401',
            '079-8454',
            null,
            true,
        ),

        // *** Hyogo ***
        //(Same as Itami)
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hyogo',
            '563-0801',
            '563-0801',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hyogo',
            '650-0000',
            '655-0896',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hyogo',
            '657-0000',
            '666-0262',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hyogo',
            '669-1101',
            '669-1548',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hyogo',
            '671-0121',
            '671-0123',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hyogo',
            '673-0000',
            '673-0049',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hyogo',
            '673-0841',
            '673-0898',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hyogo',
            '673-8686',
            '673-8686',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hyogo',
            '674-0050',
            '675-1236',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hyogo',
            '676-8501',
            '676-8501',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hyogo',
            '676-0000',
            '676-0828',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hyogo',
            '659-8501',
            '659-8501',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hyogo',
            '666-0292',
            '666-0292',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hyogo',
            '666-8501',
            '666-8501',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hyogo',
            '669-1595',
            '669-1595',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hyogo',
            '675-8501',
            '675-8501',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hyogo',
            '675-1380',
            '675-1380',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'hyogo',
            '675-1300',
            '675-1379',
            null,
            true,
        ),

        /// *** Ibaraki ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'ibaraki',
            '300-0000',
            '311-4407',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'ibaraki',
            '311-4501',
            '319-3705',
            null,
            true,
        ),

        // *** Itami ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'itami',
            '664-0000',
            '664-0899',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'itami',
            '563-0801',
            '563-0801',
            null,
            true,
        ),
        // extra for Hyogo Marketing
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'itami',
            '650-0000',
            '658-0084',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'itami',
            '659-0000',
            '659-0096',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'itami',
            '660-0000',
            '661-0985',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'itami',
            '662-0000',
            '663-8247',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'itami',
            '665-0000',
            '666-0262',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'itami',
            '669-1101',
            '669-1548',
            null,
            true,
        ),

        // *** Iwate *** 020-0000 to 029-6000
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'iwate',
            '020-0000',
            '029-6000',
            null,
            true,
        ),

        // *** Kanagawa ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'kanagawa',
            '210-0000',
            '259-9999',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'kanagawa',
            '231-8588',
            '231-8588',
            null,
            true,
        ),

        // *** Kanagawa (Business) ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-kanagawa',
            '210-0000',
            '259-9999',
            null,
            true,
        ),

        // *** Kirinnomachi ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'kirinnomachi',
            '680-0000',
            '680-0226',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'kirinnomachi',
            '680-0801',
            '680-8066',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'kirinnomachi',
            '689-0101',
            '689-0537',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'kirinnomachi',
            '689-1101',
            '689-1326',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'kirinnomachi',
            '669-6700',
            '669-6954',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'kirinnomachi',
            '680-0700',
            '680-0746',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'kirinnomachi',
            '669-6792',
            '669-6792',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'kirinnomachi',
            '680-0792',
            '680-0792',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'kirinnomachi',
            '680-8571',
            '680-8571',
            null,
            true,
        ),
        // *** Kobe ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'kobe',
            '650-0000',
            '658-0084',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'kobe',
            '669-1161',
            '669-1161',
            null,
            true,
        ),

        // *** Kuwana City ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'kuwana-city',
            '511-0000',
            '511-0125',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'kuwana-city',
            '511-0801',
            '511-1149',
            null,
            true,
        ),

        // *** Kyoto ***;
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'kyoto',
            '600-0000',
            '618-0000',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'kyoto',
            '618-0025',
            '629-3579',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'kyoto',
            '520-0461',
            '520-0465',
            null,
            true,
        ),

        /// *** Mie ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'mie',
            '498-0000',
            '498-0000',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'mie',
            '498-0801',
            '498-0824',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'mie',
            '510-0000',
            '519-5835',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'mie',
            '647-1321',
            '647-1325',
            null,
            true,
        ),

        /// *** Mie (Business) ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-mie',
            '510-0000',
            '510-0107',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-mie',
            '510-0301',
            '510-0971',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-mie',
            '510-8001',
            '510-8039',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-mie',
            '511-0000',
            '511-0125',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-mie',
            '511-0801',
            '512-8066',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-mie',
            '514-0000',
            '514-2328',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-mie',
            '515-2501 ',
            '515-3536',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-mie',
            '514-8611 ',
            '514-8611',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-mie',
            '510-8601 ',
            '510-8601',
            null,
            true,
        ),

        /// *** Miyagi ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'miyagi',
            '980-0000',
            '989-6941',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'miyagi',
            '980-8570',
            '980-8570',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'miyagi',
            '980-8671',
            '980-8671',
            null,
            true,
        ),

        /// *** Miyagi (Business) ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-miyagi',
            '980-0000',
            '989-6941',
            null,
            true,
        ),

        /// *** Miyoshi-city ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'miyoshi-city',
            '470-0200',
            '470-0233',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'miyoshi-city',
            '470-0295',
            '470-0295',
            null,
            true,
        ),

        // *** Nagano ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'nagano',
            '380-0801',
            '389-2104',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'nagano',
            '390-0221',
            '399-9101',
            null,
            true,
        ),

        // *** Nagasaki ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'nagasaki',
            '848-0401',
            '848-0408',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'nagasaki',
            '850-0000',
            '851-1201',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'nagasaki',
            '851-2100',
            '852-8157',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'nagasaki',
            '854-0000',
            '857-0069',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'nagasaki',
            '857-0101',
            '857-2427',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'nagasaki',
            '857-3101',
            '857-3102',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'nagasaki',
            '858-0901',
            '858-0926',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'nagasaki',
            '858-0965',
            '859-4308',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'nagasaki',
            '859-4500',
            '859-4528',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'nagasaki',
            '859-4531',
            '859-4744',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'nagasaki',
            '859-4751',
            '859-4826',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'nagasaki',
            '859-5100',
            '859-5707',
            ['859-5101'],
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'nagasaki',
            '859-6101',
            '859-6415',
            null,
            true,
        ),

        // *** Nagasaki (Business) ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nagasaki',
            '848-0401',
            '848-0408',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nagasaki',
            '850-0000',
            '851-1201',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nagasaki',
            '851-2100',
            '852-8157',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nagasaki',
            '854-0000',
            '857-0069',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nagasaki',
            '857-0101',
            '857-2427',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nagasaki',
            '857-3101',
            '857-3102',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nagasaki',
            '858-0901',
            '858-0926',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nagasaki',
            '858-0965',
            '859-4308',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nagasaki',
            '859-4500',
            '859-4528',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nagasaki',
            '859-4531',
            '859-4744',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nagasaki',
            '859-4751',
            '859-4826',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nagasaki',
            '859-5100',
            '859-5100',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nagasaki',
            '859-5102',
            '859-5707',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nagasaki',
            '859-6101',
            '859-6415',
            null,
            true,
        ),

        // *** Aomori *** //
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'aomori',
            '018-5501',
            '018-5501',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'aomori',
            '030-0111',
            '039-5346',
            null,
            true,
        ),

        // *** Saitama *** //
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'saitama',
            '330-0000',
            '331-0825',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'saitama',
            '336-0000',
            '339-0082',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'saitama',
            '174-8741',
            '174-8743',
            ['174-8742'],
            true,
        ),

        // *** Saitama (Business) *** //
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-saitama',
            '330-0000',
            '331-0825',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-saitama',
            '336-0000',
            '339-0082',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-saitama',
            '330-9588',
            '330-9588',
            null,
            true,
        ),

        // *** Wakayama *** //
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'wakayama',
            '640-0000',
            '647-1235',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'wakayama',
            '647-1600',
            '648-0263',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'wakayama',
            '648-0401',
            '649-7216',
            null,
            true,
        ),

        // *** Ishikawa *** //
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'ishikawa',
            '920-0000',
            '929-2379',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'ishikawa',
            '939-0171',
            '939-0171',
            null,
            true,
        ),

        // *** Niigata ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'niigata',
            '389-2261',
            '389-2261',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'niigata',
            '940-0000',
            '949-8317',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'niigata',
            '949-8401',
            '951-8167',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'niigata',
            '953-0011',
            '959-4636',
            null,
            true,
        ),

        // *** Oita ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'oita',
            '839-1421',
            '839-1421',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'oita',
            '870-0001',
            '870-1223',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'oita',
            '871-0000',
            '871-0208',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'oita',
            '871-0311',
            '871-0716',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'oita',
            '872-0000',
            '879-7885',
            null,
            true,
        ),

        // *** Okayama ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'okayama',
            '700-0001',
            '700-0986',
            null,
            true,
        ),

        new ZipcodeEntry(
            BusinessCode.SPJP,
            'okayama',
            '701-0131',
            '701-2525',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'okayama',
            '701-4200',
            '704-8196',
            null,
            true,
        ),
        // Okayama (Tamano-City)
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'okayama',
            '706-0000',
            '706-0317',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'okayama',
            '709-0400',
            '709-3145',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'okayama',
            '710-0151',
            '710-0151',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'okayama',
            '700-8570',
            '700-8570',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'okayama',
            '716-1100',
            '716-1242',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'okayama',
            '716-1551',
            '716-1554',
            null,
            true,
        ),

        // *** Osaka ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'osaka',
            '530-0000',
            '599-8280',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'osaka',
            '618-0000',
            '618-0024',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'osaka',
            '630-0271',
            '630-0272',
            null,
            true,
        ),

        // *** Sendai City ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'sendai-city',
            '980-0000',
            '980-6190',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'sendai-city',
            '981-0901',
            '981-1107',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'sendai-city',
            '981-3100',
            '981-3227',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'sendai-city',
            '981-8001',
            '984-0845',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'sendai-city',
            '985-0901',
            '985-0901',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'sendai-city',
            '989-3121',
            '989-3434',
            null,
            true,
        ),

        /// *** Shizuoka ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'shizuoka',
            '410-0000',
            '431-4112',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'shizuoka',
            '432-0000',
            '439-0037',
            null,
            true,
        ),

        /// *** Shizuoka (Business) ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-shizuoka',
            '410-0000',
            '431-4112',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-shizuoka',
            '432-0000',
            '439-0037',
            null,
            true,
        ),

        /// *** Tochigi ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'tochigi',
            '311-4411',
            '311-4411',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'tochigi',
            '320-0001',
            '329-4425',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'tochigi',
            '349-1221',
            '349-1221',
            null,
            true,
        ),

        /// *** Tokyo ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'tokyo',
            '100-0000',
            '208-0035',
            ['$1000015', '1006000$', '$1980226', '2009999$'],
            true,
        ),

        // *** Toyota City ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'toyota-city',
            '441-2511',
            '441-2526',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'toyota-city',
            '444-2201',
            '444-2847',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'toyota-city',
            '444-3201',
            '444-3265',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'toyota-city',
            '470-0301',
            '470-0575',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'toyota-city',
            '470-1201',
            '470-1219',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'toyota-city',
            '471-0000',
            '471-0879',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'toyota-city',
            '473-0901',
            '473-0939',
            null,
            true,
        ),

        // *** Tottori – City ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'tottori-city',
            '680-0000',
            '680-0226',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'tottori-city',
            '680-0801',
            '680-8066',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'tottori-city',
            '689-0101',
            '689-0537',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'tottori-city',
            '689-1101',
            '689-1326',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'tottori-city',
            '680-8571',
            '680-8571',
            null,
            true,
        ),

        // *** B2B Yamaguchi ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-yamaguchi',
            '740-0000',
            '740-0045',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-yamaguchi',
            '740-0061',
            '742-0036',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-yamaguchi',
            '742-0111',
            '742-1400',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-yamaguchi',
            '742-1402',
            '742-1403',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-yamaguchi',
            '742-1500',
            '742-1515',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-yamaguchi',
            '742-1517',
            '742-2107',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-yamaguchi',
            '742-2301',
            '742-2518',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-yamaguchi',
            '742-2602',
            '742-2716',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-yamaguchi',
            '742-2802',
            '743-0002',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-yamaguchi',
            '743-0004',
            '745-0056',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-yamaguchi',
            '745-0061',
            '747-0831',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-yamaguchi',
            '747-0833',
            '750-0094',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-yamaguchi',
            '750-0251',
            '758-0000',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-yamaguchi',
            '758-0011',
            '758-0613',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-yamaguchi',
            '759-0121',
            '759-6541',
            null,
            true,
        ),
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-yamaguchi',
            '759-6601',
            '759-6615',
            null,
            true,
        ),

        // *** Yamaguchi ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'yamaguchi',
            '740-0000',
            '759-6615',
            null,
            true,
        ),

        // *** Yamanashi ***
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'yamanashi',
            '400-0000',
            '409-3867',
            null,
            true,
        ),

        // *** NATS (Business) ***
        // Toyonaka city
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nats',
            '560-0000',
            '561-0894',
            null,
            true,
        ),
        // Suita city
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nats',
            '564-0000',
            '565-0875',
            null,
            true,
        ),
        // Nishinomiya city
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nats',
            '651-1411',
            '651-1433',
            null,
            true,
        ),
        // Nishinomiya city
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nats',
            '662-0000',
            '663-8247',
            null,
            true,
        ),
        // Nishinomiya city
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nats',
            '669-1101',
            '669-1149',
            null,
            true,
        ),
        // Nishinomiya city
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nats',
            '669-1162',
            '669-1162',
            null,
            true,
        ),
        // Nishinomiya city
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nats',
            '669-1251',
            '669-1251',
            null,
            true,
        ),
        // Amagasaki city
        new ZipcodeEntry(
            BusinessCode.SPJP,
            'business-nats',
            '660-0000',
            '661-0985',
            null,
            true,
        ),
    ];

    private selectableCommunities$: Observable<string[]> = of(
        JapanAddressService.TmgCommunities,
    );

    constructor(
        protected http: HttpClient,
        @Inject(ADDRESS_SERVICE_URL) private addressUrl: string,
        @Inject(API_URL) private apiUrl: string,
        @Inject(BUSINESS) private business: string,
        private communityService: CommunityService,
    ) {}

    getAddress(zipCode: string): Observable<JapanAddress> {
        return this.getAddressResponse(zipCode.replace('-', '')).pipe(
            map(this.mapAddressResponseToAddress),
        );
    }

    private getAddressResponse(zipCode: string): Observable<AddressResponse> {
        return this.http.get<AddressResponse>(`${this.addressUrl}${zipCode}`);
    }

    private mapAddressResponseToAddress(
        response: AddressResponse,
    ): JapanAddress {
        const result = new JapanAddress();

        const sublocalities_INVALID_STRING = [
            '（次のビルを除く）', // Should be removed from sublocalities array item
        ];
        result.area = response.prefecture;
        result.englishPrefecture = response.englishPrefecture;
        result.englishSublocalities = response.englishSublocalities;
        result.englishWard = response.englishWard;

        result.sublocalities = ['', '', '', ''];
        response.sublocalities.forEach((v, i) => {
            if (i < 4) {
                const sanitisedSublocality = sublocalities_INVALID_STRING
                    .map((value) =>
                        v.includes(value)
                            ? v.replace(value, '').replace(' ', '')
                            : v,
                    )
                    .toString();
                result.sublocalities[i] = sanitisedSublocality;
            }
        });
        return result;
    }

    private mapBusiness(business: string): BusinessCode {
        switch (business.toLowerCase()) {
            case 'enjp':
                return BusinessCode.ENJP;
            case 'spjp':
                return BusinessCode.SPJP;
            default:
                return BusinessCode.ALL;
        }
    }

    public getCityParts(address: JapanAddress): string {
        return [address.city]
            .concat(address.sublocalities.slice(0, 3))
            .filter(
                (loc) => !!loc && !this.sublocalities_BLACKLIST.includes(loc),
            )
            .join(' ');
    }

    public isSupportedZip(zipCode: string): Boolean {
        const zipPattern = /^\d{3}-\d{4}$/;
        if (!zipCode.match(zipPattern)) {
            return false;
        }

        const businessCode = this.mapBusiness(this.business);
        let result = false;
        for (const entry of this.zipMatrix) {
            if (
                entry.match(
                    businessCode,
                    this.communityService.communityCode,
                    zipCode,
                )
            ) {
                result = true;
                break;
            }
        }
        return result;
    }

    public getAlternateCommunityCodesForZip(zipCode: string): string[] {
        const zipPattern = /^\d{3}-\d{4}$/;
        if (!zipCode.match(zipPattern)) {
            return null;
        }

        const businessCode = this.mapBusiness(this.business);
        const result = [];
        for (const entry of this.zipMatrix) {
            const matchedCommunity = entry.getCommunityWhenMatching(
                businessCode,
                zipCode,
            );
            if (
                matchedCommunity &&
                entry.proposeAsAlternate &&
                !result.includes(matchedCommunity)
            ) {
                result.push(matchedCommunity);
            }
        }
        return result;
    }

    public showSelectors$(communityCode: string): Observable<boolean> {
        // TODO: Replace selectableCommunities$ by API call in the future
        return this.selectableCommunities$.pipe(
            map((communityCodes) =>
                communityCodes.some((c) => c === communityCode),
            ),
        );
    }

    public getCities$(): Observable<City[]> {
        return this.http.get<City[]>(`${this.apiUrl}cities/`);
    }

    public getPrefectures$(): Observable<Prefecture[]> {
        return this.http.get<Prefecture[]>(`${this.apiUrl}prefectures/`);
    }

    public getFirstPrefectureById$(
        prefectureId: string,
    ): Observable<Prefecture> {
        return this.getPrefectures$().pipe(
            map((prefectures) => {
                const matching = prefectures.filter(
                    (p) => p.id === prefectureId,
                );
                if (matching.length > 0) {
                    return matching[0];
                }
                return null;
            }),
        );
    }

    public getFirstPrefectureByCommunityCode$(
        communityCode: string,
    ): Observable<Prefecture> {
        return this.getPrefectures$().pipe(
            map((prefectures) => {
                const matching = prefectures.filter(
                    (p) => p.community?.code === communityCode,
                );
                if (matching.length > 0) {
                    return matching[0];
                }
                return null;
            }),
        );
    }

    public getFirstCityByCommunityCode$(
        communityCode: string,
    ): Observable<City> {
        return this.getCities$().pipe(
            map((cities) => {
                const matching = cities.filter(
                    (c) => c.community?.code === communityCode,
                );
                if (matching.length > 0) {
                    return matching[0];
                }
                return null;
            }),
        );
    }
}

export interface AddressResponse {
    zip: string;
    city: string;
    prefecture: string;
    sublocalities: string[];

    englishPrefecture: string;
    englishSublocalities: string;
    englishWard: string;
}

// All ranges are inclusive <min, max> !!!
class ZipcodeEntry {
    private businessCode: BusinessCode;
    private communityCode: string;
    private startRange: { prefix: number; suffix: number };
    private endRange: { prefix: number; suffix: number };
    private excluded: { prefix: number; suffix: number }[];
    private _proposeAsAlternate: boolean;

    get proposeAsAlternate(): boolean {
        return this._proposeAsAlternate;
    }

    constructor(
        businessCode: BusinessCode,
        communityCode: string,
        startRangeZip: string,
        endRangeZip: string,
        excluded: string[],
        proposeAsAlternate: boolean,
    ) {
        this.businessCode = businessCode;
        this.communityCode = communityCode.toLowerCase();
        this.startRange = this.splitZipCode(startRangeZip);
        this.endRange = this.splitZipCode(endRangeZip);

        if (excluded !== null) {
            if (excluded.find((e) => e.includes('$'))) {
                excluded = joinRanges(excluded);
            }

            this.excluded = excluded.map((zip) => this.splitZipCode(zip));
        }

        this._proposeAsAlternate = proposeAsAlternate;
    }

    match(
        businessCode: BusinessCode,
        communityCode: string,
        zipCode: string,
    ): boolean {
        const splitZip = this.splitZipCode(zipCode);

        return (
            (this.businessCode === businessCode ||
                this.businessCode === BusinessCode.ALL) &&
            communityCode.toLowerCase() === this.communityCode &&
            this.isNotExcluded(splitZip.prefix, splitZip.suffix) &&
            this.isStartMatched(splitZip.prefix) &&
            this.isEndMatched(splitZip.prefix, splitZip.suffix)
        );
    }

    getCommunityWhenMatching(
        businessCode: BusinessCode,
        zipCode: string,
    ): string {
        const splitZip = this.splitZipCode(zipCode);

        if (
            (this.businessCode === businessCode ||
                this.businessCode === BusinessCode.ALL) &&
            this.isNotExcluded(splitZip.prefix, splitZip.suffix) &&
            this.isStartMatched(splitZip.prefix) &&
            this.isEndMatched(splitZip.prefix, splitZip.suffix)
        ) {
            return this.communityCodeMapping(businessCode, this.communityCode);
        }
        return null;
    }

    private communityCodeMapping(
        businessCode: BusinessCode,
        communityCode: string,
    ): string {
        const key =
            BusinessCode[businessCode].toLowerCase() +
            '-' +
            communityCode.toLowerCase();
        return JapanAddressService.CommunityMapping[key] ?? communityCode;
    }

    private isNotExcluded(zipCodeStart: number, zipCodeEnd: number): boolean {
        let result = true;
        if (this.excluded) {
            for (const ex of this.excluded) {
                if (zipCodeStart === ex.prefix && zipCodeEnd === ex.suffix) {
                    result = false;
                    break;
                }
            }
        }

        return result;
    }

    private isStartMatched(zipCodeStart: number): boolean {
        return (
            zipCodeStart >= this.startRange.prefix &&
            zipCodeStart <= this.endRange.prefix
        );
    }

    private isEndMatched(zipCodeStart: number, zipCodeEnd: number): boolean {
        if (
            zipCodeStart === this.startRange.prefix &&
            zipCodeStart === this.endRange.prefix
        ) {
            return (
                zipCodeEnd >= this.startRange.suffix &&
                zipCodeEnd <= this.endRange.suffix
            );
        }

        if (zipCodeStart === this.startRange.prefix) {
            return zipCodeEnd >= this.startRange.suffix;
        }

        if (zipCodeStart === this.endRange.prefix) {
            return zipCodeEnd <= this.endRange.suffix;
        }

        // Accept all zipEnds (0000 - 9999) when inside zipStarts range
        if (
            zipCodeStart > this.startRange.prefix &&
            zipCodeStart < this.endRange.prefix
        ) {
            return true;
        }

        return false;
    }

    private splitZipCode(zipCode: string): { prefix: number; suffix: number } {
        return {
            prefix: +zipCode.split('-')[0],
            suffix: +zipCode.split('-')[1],
        };
    }
}
